import { reactive } from 'vue'
import pin2d8cf0 from '/@/assets/icons/pin-2d8cf0.svg'
import { MapDoodleType } from '/@/constants/map'
import { getRoot } from '/@/root'
import { MapDoodleEnum } from '/@/types/map-enum'
import { EFlightAreaType } from '../types/flight-area'
import { message } from 'ant-design-vue'

export function useMouseTool () {
  const root = getRoot()

  let customCircle: any = null
  let customCircleCenter: any = null
  let customCircleCenterMarker: any = null
  let customCircleClickHandler: ((event: any) => void) | null = null
  let customCircleMoveHandler: ((event: any) => void) | null = null

  const state = reactive({
    pinNum: 0,
    polylineNum: 0,
    PolygonNum: 0,
    currentType: '',
  })
  const flightAreaColorMap = {
    [EFlightAreaType.DFENCE]: '#19be6b',
    [EFlightAreaType.NFZ]: '#ff0000',
  }

  function getEventLngLat (event: any) {
    return event?.lnglat || event?.lngLat
  }

  function getCircleRadius (center: any, edge: any) {
    if (typeof center?.distance === 'function') {
      return center.distance(edge)
    }
    return root?.$aMap.GeometryUtil.distance([center, edge])
  }

  function stopTwoClickCircle (removeCircle = true) {
    if (customCircleClickHandler) {
      root?.$map.off('click', customCircleClickHandler)
    }
    if (customCircleMoveHandler) {
      root?.$map.off('mousemove', customCircleMoveHandler)
    }
    if (customCircleCenterMarker) {
      root?.$map.remove(customCircleCenterMarker)
    }
    if (removeCircle && customCircle) {
      root?.$map.remove(customCircle)
    }
    root?.$map.setDefaultCursor('default')
    customCircle = null
    customCircleCenter = null
    customCircleCenterMarker = null
    customCircleClickHandler = null
    customCircleMoveHandler = null
  }
  function drawPin (type:MapDoodleType, getDrawCallback:Function) {
    root?.$mouseTool.marker({
      title: type + state.pinNum,
      icon: pin2d8cf0,
    })
    state.pinNum++
    root?.$mouseTool.on('draw', getDrawCallback)
  }

  function drawPolyline (type:MapDoodleType, getDrawCallback:Function) {
    root?.$mouseTool.polyline({
      strokeColor: '#2d8cf0',
      strokeOpacity: 1,
      strokeWeight: 2,
      strokeStyle: 'solid',
      title: type + state.polylineNum++
    })
    root?.$mouseTool.on('draw', getDrawCallback)
  }

  function drawPolygon (type:MapDoodleType, getDrawCallback:Function) {
    root?.$mouseTool.polygon({
      strokeColor: '#2d8cf0',
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: '#1791fc',
      fillOpacity: 0.4,
      title: type + state.PolygonNum++
    })
    root?.$mouseTool.on('draw', getDrawCallback)
  }

  function drawOff (type:MapDoodleType) {
    stopTwoClickCircle()
    root?.$mouseTool.close()
    root?.$mouseTool.off('draw')
  }

  function drawFlightAreaPolygon (type: EFlightAreaType, getDrawFlightAreaCallback: Function) {
    root?.$mouseTool.polygon({
      strokeColor: flightAreaColorMap[type],
      strokeOpacity: 1,
      strokeWeight: 4,
      extData: {
        type: type,
        mapType: 'polygon',
      },
      strokeStyle: 'dashed',
      strokeDasharray: EFlightAreaType.NFZ === type ? [10, 2] : [10, 1, 2],
      fillColor: flightAreaColorMap[type],
      fillOpacity: EFlightAreaType.NFZ === type ? 0.3 : 0,
    })
    root?.$mouseTool.on('draw', getDrawFlightAreaCallback)
  }

  function drawFlightAreaCircle (type: EFlightAreaType, getDrawFlightAreaCallback: Function) {
    const circleOptions = {
      strokeColor: flightAreaColorMap[type],
      strokeOpacity: 1,
      strokeWeight: 6,
      extData: {
        type: type,
        mapType: 'circle',
      },
      strokeStyle: 'dashed',
      strokeDasharray: EFlightAreaType.NFZ === type ? [10, 2] : [10, 1, 2],
      fillColor: flightAreaColorMap[type],
      fillOpacity: EFlightAreaType.NFZ === type ? 0.3 : 0,
    }

    root?.$map.setDefaultCursor('crosshair')
    customCircleClickHandler = (event: any) => {
      const lngLat = getEventLngLat(event)
      if (!lngLat) {
        return
      }
      if (!customCircleCenter) {
        customCircleCenter = lngLat
        customCircle = new root.$aMap.Circle({
          ...circleOptions,
          center: customCircleCenter,
          radius: 0,
        })
        customCircleCenterMarker = new root.$aMap.CircleMarker({
          center: customCircleCenter,
          radius: 5,
          strokeWeight: 2,
          strokeColor: '#ffffff',
          fillColor: flightAreaColorMap[type],
          fillOpacity: 1,
          zIndex: 120,
          bubble: true,
        })
        root.$map.add([customCircle, customCircleCenterMarker])
        return
      }

      customCircle.setRadius(getCircleRadius(customCircleCenter, lngLat))
      const completedCircle = customCircle
      stopTwoClickCircle(false)
      getDrawFlightAreaCallback({ obj: completedCircle })
    }
    customCircleMoveHandler = (event: any) => {
      const lngLat = getEventLngLat(event)
      if (!customCircle || !customCircleCenter || !lngLat) {
        return
      }
      customCircle.setRadius(getCircleRadius(customCircleCenter, lngLat))
    }
    root?.$map.on('click', customCircleClickHandler)
    root?.$map.on('mousemove', customCircleMoveHandler)
  }

  function mouseTool (type: MapDoodleType, getDrawCallback: Function, flightAreaType?: EFlightAreaType) {
    stopTwoClickCircle()
    root?.$mouseTool.close()
    root?.$mouseTool.off('draw')
    state.currentType = type
    if (flightAreaType) {
      switch (type) {
        case MapDoodleEnum.POLYGON:
          drawFlightAreaPolygon(flightAreaType, getDrawCallback)
          return
        case MapDoodleEnum.CIRCLE:
          drawFlightAreaCircle(flightAreaType, getDrawCallback)
          return
        default:
          message.error(`Invalid type: ${flightAreaType}`)
          return
      }
    }
    switch (type) {
      case MapDoodleEnum.PIN:
        drawPin(type, getDrawCallback)
        break
      case MapDoodleEnum.POLYLINE:
        drawPolyline(type, getDrawCallback)
        break
      case MapDoodleEnum.POLYGON:
        drawPolygon(type, getDrawCallback)
        break
      case MapDoodleEnum.Close:
        drawOff(type)
        break
    }
  }

  return {
    mouseTool
  }
}
